
    import Vue from "vue";
    import {Component, Prop, Watch} from "vue-property-decorator";
    import {VueInject} from "@/VueIntegration/VueInjectable";
    import {Slideshow} from "@/slideshow";
    import { TutorialAttemptApi } from "@/Api/TutorialAttemptApi";
    import VueButton from "../UI/VueButton.vue";
    import Modal from "../UI/Modal.vue";
    import ResultModal from "./ResultModal.vue";
    import {render} from "@/Core/CustomisationRenderer";
    import {announce} from "@/UI/Announce";
    import {IAskMultipleChoiceQuestion, ICompletedMultipleChoiceQuestion} from "@/Core/Interactions";
    import {IAppearance} from "@/Core/TutorialVariables";
    import {TutorialState} from "@/Core/TutorialState";

    @Component({
        components: {
            Modal,
            ResultModal,
            VueButton
        }
    })
    export default class Website extends Vue {
        @Prop({}) id: string;
        @Prop({}) url: string;
        @Prop({}) isSecure: boolean;
        @Prop({}) contents: string;
        @Prop({type: Boolean, required: true}) feedback: boolean;

        //Services
        @VueInject("Slideshow") readonly slideshow: Slideshow;
        @VueInject("TutorialAttemptApi") readonly api: TutorialAttemptApi;
        @VueInject("TutorialState") state: TutorialState;

        //State
        question: IAskMultipleChoiceQuestion|null = null;
        options: {itemText: string, code: string}[] = [];
        selectedCodes: string[] = [];
        completedQuestion: ICompletedMultipleChoiceQuestion|null = null;
        showResultModal = false;
        appearance: IAppearance;
        isSubmittingAnswers = false;
        errorMessage: string|null = null;
        lockId: number|null = null;
        showAccessibilityInfo = false;

        get isAnswered() {
            return this.completedQuestion !== null;
        }

        get canSelectAnswer() {
            return !this.isSubmittingAnswers
                && this.completedQuestion === null
                && this.selectedCodes.length < (this.question?.maxAnswers ?? 0);
        }

        get renderedContents() {
            return render(this.contents, this.state);
        }

        get renderedUrl() {
            return render(this.url, this.state);
        }

        mounted() {
            this.enter();
        }

        @Watch("id")
        enter() {
            this.options = [];
            this.selectedCodes = [];
            this.showResultModal = false;
            this.isSubmittingAnswers = false;
            this.errorMessage = null;
            this.lockId = null;
            this.appearance = this.slideshow.appearance;
            this.question = <IAskMultipleChoiceQuestion> this.api.getQuestion(this.id);
            for (let code in this.question.items) {
                let item = this.question.items[code];
                this.options.push({
                    itemText: item.itemText,
                    code: item.code
                });
            }

            this.completedQuestion = <ICompletedMultipleChoiceQuestion> this.api.getCompletedQuestion(this.id);
            if (this.completedQuestion) {
                this.selectedCodes = this.completedQuestion.givenAnswers.map(a => a.questionAnswerCode);
                this.$nextTick(() => this.mark());
                announce("Slide featuring a website question that has been completed.");
            }
            else {
                this.lockId = this.slideshow.lockNext();
                this.$nextTick(() => this.registerClickListeners());
                announce("Slide featuring a website question to answer. Please go to the main region of the page.");
            }

            this.slideshow.slideEntered();
        }

        registerClickListeners() {
            this.cleanElements();
            let contentsElement = <HTMLElement> this.$refs['contents'];
            let clickableElements = contentsElement.querySelectorAll("[code]");
            clickableElements.forEach(e => {
                this.setUpAnswer(<HTMLElement> e);
            });

            this.setUpAnswer(<HTMLElement> this.$refs['url']);
        }

        private elements: {
            element: HTMLElement,
            click: (event: MouseEvent) => any,
            keyup: (event: Event) => any
        }[] = [];

        setUpAnswer(e: HTMLElement) {
            this.setChecked(<HTMLElement> e, false);
            let clickHandler = (event: MouseEvent) => {
                this.toggle(<HTMLElement> e);
                event.preventDefault();
                event.stopPropagation();
            };
            let keyHandler = (event: Event) => {
                this.toggle(<HTMLElement> e);
                event.preventDefault();
                event.stopPropagation();
            };

            e.addEventListener("click", clickHandler);
            e.addEventListener("keyup", keyHandler);
            this.elements.push({
                element: e,
                click: clickHandler,
                keyup: keyHandler
            });
        }

        cleanElements() {
            for (let e of this.elements) {
                e.element.removeEventListener("click", e.click);
                e.element.removeEventListener("keyup", e.keyup);
            }

            this.elements = [];
        }

        toggle(element: HTMLElement) {
            let existingIndex = this.selectedCodes.findIndex(x => x === element.getAttribute('code'));
            if (existingIndex === -1) {
                if (!this.canSelectAnswer) {
                    element.blur();
                    return;
                }

                this.setChecked(element, true);
                this.selectedCodes.push(<string> element.getAttribute('code'));
            }
            else {
                this.setChecked(element, false);
                this.selectedCodes.splice(existingIndex, 1);
            }
        }

        setChecked(e: HTMLElement, isChecked: boolean) {
            e.setAttribute("tabindex", "0");
            e.setAttribute("role", "checkbox");
            e.setAttribute("aria-checked", isChecked.toString());
            if (isChecked) {
                e.className += ' selected-element';
            }
            else if (e.className.indexOf('selected-element') >= 0) {
                e.className = e.className.replace('selected-element', '');
            }
        }

        mark() {
            if (this.completedQuestion == null)
                return;

            let container = <HTMLElement> this.$refs['email'];

            for (let answer of this.completedQuestion.givenAnswers) {
                let element = container.querySelector(`[code=${answer.questionAnswerCode}]`);
                if (element == null) {
                    console.warn("No answer element for code " + answer.questionAnswerCode);
                    return;
                }

                if (answer.isCorrect) {
                    element.className += " correct";
                }
                else {
                    element.className += " incorrect";
                }
            }
        }

        submitAnswers() {
            if (this.question == null || this.isSubmittingAnswers || this.completedQuestion)
                return;

            let toSubmit = this.selectedCodes;

            if (toSubmit.length > this.question.maxAnswers) {
                this.errorMessage = "Too many answers selected.";
                return;
            }

            if (toSubmit.length < this.question.minAnswers) {
                this.errorMessage = "Too few answers selected.";
                return;
            }

            this.isSubmittingAnswers = true;
            this.api.submitMultipleChoice(this.id, toSubmit)
                .then(result => {
                    this.isSubmittingAnswers = false;
                    this.completedQuestion = result;
                    if (this.lockId)
                        this.slideshow.unlockNext(this.lockId);

                    if (this.feedback) {
                        this.showResultModal = true;
                        this.mark();
                    }
                    else {
                        this.slideshow.nextSlide();
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.errorMessage = "Sorry, there's been an error processing your request. " +
                        "Please try refreshing this page and attempting to answer the question again. " +
                        "If the error persists, please contact " +
                        this.slideshow.variables.customer.supportEmail;
                });
        }

        finished() {
            this.slideshow.nextSlide();
        }
    }
